import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';
import { getAuth } from 'firebase/auth';
import {
  subscribeExpiredLicenses,
  acknowledgeExpiredLicense,
  getNotificationPreferences,
  ExpiredLicense,
} from '../../services/notification.service';

interface ExpiredLicensesModalProps {
  firebase: any;
}

const ExpiredLicensesModal: React.FC<ExpiredLicensesModalProps> = ({ firebase }) => {
  const [expiredLicenses, setExpiredLicenses] = useState<ExpiredLicense[]>([]);
  const [loading, setLoading] = useState(false);
  const [acknowledgingIds, setAcknowledgingIds] = useState<Set<string>>(new Set());
  const [acknowledgingAll, setAcknowledgingAll] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const { currentOrgId } = useOrganizationStore();

  // Use notifications store for modal visibility and counts
  const isOpen = useNotificationsStore(state => state.showExpiredLicensesModal);
  const setIsOpen = useNotificationsStore(state => state.setShowExpiredLicensesModal);
  const setExpiredLicensesCount = useNotificationsStore(state => state.setExpiredLicensesCount);

  // Reset auto-open flag when org changes
  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  // Live subscription combining expired + revoked licenses for this client org.
  // Gated on the user's in-app preference for `expiredLicenses`.
  useEffect(() => {
    if (!currentOrgId || !firebase) {
      setExpiredLicenses([]);
      setExpiredLicensesCount(0);
      return;
    }

    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    setLoading(true);
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const prefs = await getNotificationPreferences(firebase, currentOrgId, userId);
      if (cancelled) return;
      if (!prefs.inApp.expiredLicenses) {
        setExpiredLicenses([]);
        setExpiredLicensesCount(0);
        setLoading(false);
        return;
      }

      let autoOpenedThisOrg = false;
      unsubscribe = subscribeExpiredLicenses(firebase, currentOrgId, (uniqueExpired) => {
        setExpiredLicenses(uniqueExpired);
        setExpiredLicensesCount(uniqueExpired.length);
        setLoading(false);

        if (!autoOpenedThisOrg && !hasAutoOpened && uniqueExpired.length > 0) {
          autoOpenedThisOrg = true;
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      });
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [currentOrgId, firebase, hasAutoOpened, setExpiredLicensesCount, setIsOpen]);

  const handleAcknowledge = async (license: ExpiredLicense) => {
    if (!firebase) return;

    setAcknowledgingIds((prev) => new Set(prev).add(license.id));

    try {
      // List + bell count update via the subscription once Firestore mutates.
      await acknowledgeExpiredLicense(firebase, {
        sourceOrgId: license.sourceOrgId,
        trackId: license.trackId,
        licenseId: license.licenseId,
        recipientRole: license.recipientRole,
      });
    } catch (error) {
      console.error('Error acknowledging expired license:', error);
    } finally {
      setAcknowledgingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(license.id);
        return newSet;
      });
    }
  };

  const handleAcknowledgeAll = async () => {
    if (!firebase) return;

    setAcknowledgingAll(true);

    try {
      await Promise.all(
        expiredLicenses.map((license) =>
          acknowledgeExpiredLicense(firebase, {
            sourceOrgId: license.sourceOrgId,
            trackId: license.trackId,
            licenseId: license.licenseId,
            recipientRole: license.recipientRole,
          })
        )
      );
    } catch (error) {
      console.error('Error acknowledging all licenses:', error);
    } finally {
      setAcknowledgingAll(false);
    }
  };

  const handleRemindLater = () => {
    setIsOpen(false);
  };

  // Close modal when all items are processed
  useEffect(() => {
    if (isOpen && expiredLicenses.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [expiredLicenses.length, isOpen, loading, setIsOpen]);

  const formatLicenseType = (type: string) => {
    switch (type) {
      case 'blanket':
        return 'Blanket License';
      case '1year':
        return '1 Year';
      case '2year':
        return '2 Years';
      case '3year':
        return '3 Years';
      case 'custom':
        return 'Custom Duration';
      default:
        return type;
    }
  };

  const getExpiryInfo = (license: ExpiredLicense) => {
    if (license.deactivatedAt) {
      return `Revoked on ${moment(license.deactivatedAt).format('DD/MM/YYYY')}`;
    }
    if (license.dateExpires) {
      return `Expired on ${moment(license.dateExpires).format('DD/MM/YYYY')}`;
    }
    return 'Expired';
  };

  if (!isOpen || expiredLicenses.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleRemindLater}
      style={{
        color: '#ffffff',
        maxWidth: '600px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleRemindLater}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={handleRemindLater}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          License Expiration Notice ({expiredLicenses.length})
        </span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="light" />
          </div>
        ) : (
          <>
            <p style={{ marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
              The following licenses have expired or been revoked. Includes licenses your organization holds AND licenses you issued to other organizations.
            </p>
            <div className="expired-licenses-list">
              {expiredLicenses.map((license) => {
                const roleLabel = license.recipientRole === 'artist'
                  ? (license.otherPartyOrgName ? `Issued to: ${license.otherPartyOrgName}` : 'Issued by you')
                  : (license.sourceOrgName ? `From: ${license.sourceOrgName}` : '');
                const rowKey = `${license.id}:${license.recipientRole}`;
                return (
                  <div
                    key={rowKey}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem 1rem',
                      marginBottom: '0.5rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 500,
                          marginBottom: '0.25rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {license.trackName || 'Unknown Track'}
                      </div>
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: 'rgba(255, 255, 255, 0.5)',
                        }}
                      >
                        {roleLabel && `${roleLabel} | `}
                        {formatLicenseType(license.licenseType)} | {getExpiryInfo(license)}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="btn-secondary-custom ml-3"
                      onClick={() => handleAcknowledge(license)}
                      disabled={acknowledgingIds.has(license.id) || acknowledgingAll}
                      style={{ minWidth: '80px' }}
                    >
                      {acknowledgingIds.has(license.id) ? (
                        <Spinner size="sm" />
                      ) : (
                        'Dismiss'
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'space-between',
        }}
      >
        <Button
          className="btn-secondary-custom"
          onClick={handleRemindLater}
          disabled={acknowledgingAll}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>Remind Me Later</span>
        </Button>
        <Button
          className="btn-primary-custom"
          onClick={handleAcknowledgeAll}
          disabled={acknowledgingAll || expiredLicenses.length === 0}
        >
          {acknowledgingAll ? (
            <>
              <Spinner size="sm" className="mr-2" /> Dismissing...
            </>
          ) : (
            <span style={{ position: 'relative', zIndex: 2 }}>
              Dismiss All ({expiredLicenses.length})
            </span>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExpiredLicensesModal;
